import { CSSProperties, memo, useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidV4 } from 'uuid';
import { components } from 'react-select';

import { IEventTag } from '@/types/projects.types';
import generateColorShades from '@/utils/colors/generate-color-shades';
import randomArrayPicker from '@/utils/random-array-picker';
import { EPaletteModes } from '@/types/theme-packs.types';
import { ReactSelectInstance } from '@ui/select/react-select/custom-react-select.types';
import { assertIsCustomReactOption } from '@/utils/assertions/assert-is-custom-react-option';
import { TagOption, TOnMenuCloseOptions } from './tags-select.types';
import TagsSelectColorPicker from './tags-select-color-picker';
import TagsOption from './tags-option';
import tagsSelectHandleChange from './tags-select-handle-change';
import tagsSelectOnEnterKeyDown from './tags-select-on-enter-key-down';
import EditStateSelect from '@ui/edit-state-wrapper/edit-state-select';

type TagsSelectProps = {
  tags: IEventTag[];
  onMenuClose?: (selectedTags: IEventTag[], options?: TOnMenuCloseOptions) => void;
  disabled?: boolean;
  onChange?: (selectedTags: IEventTag[]) => void;
  presetColors?: string[];
  themeMode?: EPaletteModes;
  viewableItems?: number;
  dynamicMenuPlacement?: boolean;
  dynamicMenuPlacementOffset?: number;
  menuListMaxHeight?: CSSProperties['maxHeight'];
};

const TagsSelect = memo(
  ({
    themeMode = EPaletteModes.Light,
    tags,
    onMenuClose,
    disabled,
    onChange,
    presetColors = [],
    dynamicMenuPlacementOffset,
    dynamicMenuPlacement,
  }: TagsSelectProps) => {
    const selectRef = useRef<ReactSelectInstance>(null);
    const selectWrapperRef = useRef<HTMLDivElement>(null);
    const draggableRef = useRef<HTMLDivElement>(null);

    const [selectedTags, setSelectedTags] = useState<TagOption[]>([]);
    const [displayColorPicker, setDisplayColorPicker] = useState<IEventTag | null>(null);
    const [workingTag, setWorkingTag] = useState<IEventTag>({
      name: '',
      color: '#000000',
      uuid: '',
    });

    useEffect(() => {
      const _tags = (tags || [])?.map((tag) => ({
        label: tag.name,
        value: tag.uuid,
        meta: tag,
      }));
      setSelectedTags(_tags);
    }, [tags]);

    const handleAdd = useCallback(
      (newTagName?: string) => {
        if (!workingTag) return;
        if (!workingTag.color) return;
        const tagName = newTagName || workingTag?.name?.trim();
        if (!tagName.length) return;

        const tagNameExists = tags.find((tag) => tag.name.trim().toLowerCase() === tagName.toLowerCase());
        if (tagNameExists) return;

        const workingTagUuid = uuidV4();
        const randomColorFromPalette = randomArrayPicker<string>(presetColors.length ? presetColors : ['#000000']);
        const shades = generateColorShades({ color: randomColorFromPalette, theme: themeMode });
        const color = randomArrayPicker<string>(shades);
        const newTag: IEventTag = {
          ...workingTag,
          name: tagName,
          color,
          uuid: workingTagUuid,
        };

        const updatedTags = [...tags, newTag];
        onChange?.(updatedTags);

        setSelectedTags((prev) => [
          ...prev,
          {
            label: newTag.name,
            value: newTag.uuid,
            meta: newTag,
          },
        ]);

        setDisplayColorPicker(null);
        setWorkingTag({
          name: '',
          color: '#000000',
          uuid: '',
        });
      },
      [workingTag, tags, presetColors, themeMode, onChange]
    );

    return (
      <div data-component="TagsSelect" ref={selectWrapperRef}>
        <EditStateSelect
          disabled={disabled}
          Label="TAGS"
          ref={selectRef}
          placeholder="Add tags"
          isCreatable
          dynamicMenuPlacementOffset={dynamicMenuPlacementOffset}
          dynamicMenuPlacement={dynamicMenuPlacement}
          onMenuClose={() => {
            if (displayColorPicker) return;
            onMenuClose?.(selectedTags.map((t) => t.meta));
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              tagsSelectOnEnterKeyDown({
                handleAdd,
                setDisplayColorPicker,
                setWorkingTag,
                draggableRef,
                workingTag,
                selectRef,
              });

              return;
            }

            // preventDefault on spacebar because spacebar selects values in react-select
            // however, we then need to manually add a space to the input value
            if (e.key === ' ') {
              e.preventDefault();
              const select = selectRef.current;
              if (select) {
                select?.onInputChange((select?.props?.inputValue || '') + ' ', {
                  action: 'set-value',
                  prevInputValue: select?.props?.inputValue || '',
                });
              }
            }
          }}
          onChange={(newValue, actionMeta) => {
            tagsSelectHandleChange({
              newValue,
              actionMeta,
              handleAdd,
              onChange,
              setSelectedTags,
              setDisplayColorPicker,
            });
          }}
          onInputChange={(inputValue) => {
            setWorkingTag((prev) => ({
              ...prev,
              name: inputValue,
            }));
          }}
          options={
            tags?.map((t) => ({
              label: t.name,
              value: t.uuid,
              meta: t,
            })) || []
          }
          // defaultValue={tags.map((tag) => ({ label: tag.name, value: tag.uuid, meta: tag }))}
          value={tags.map((tag) => ({ label: tag.name, value: tag.uuid, meta: tag }))}
          Components={{
            Option: (props) => {
              const option = props.data;
              try {
                assertIsCustomReactOption(option);
              } catch (error) {
                console.error('Error asserting custom react option', error);
                return <></>;
              }

              const meta = (option.meta || null) as IEventTag | null;
              const isFocused = props?.isFocused;
              return (
                <components.Option
                  {...props}
                  innerProps={{
                    ...props.innerProps,
                    style: { padding: 0 },
                    // hijack and block handlers as we need to handle them ourselves for the color popover
                    onClick: () => null,
                    onMouseDown: () => null,
                    onPointerDown: () => null,
                    onKeyDown: () => null,
                  }}
                >
                  <TagsOption
                    meta={meta}
                    displayColorPicker={displayColorPicker}
                    option={option}
                    workingTag={workingTag}
                    handleAdd={handleAdd}
                    selectRef={selectRef}
                    setDisplayColorPicker={setDisplayColorPicker}
                    setWorkingTag={setWorkingTag}
                    isFocused={isFocused}
                  />
                </components.Option>
              );
            },
          }}
        />
        <TagsSelectColorPicker
          selectWrapperRef={selectWrapperRef}
          draggableRef={draggableRef}
          displayColorPicker={displayColorPicker}
          setDisplayColorPicker={setDisplayColorPicker}
          workingTag={workingTag}
          setWorkingTag={setWorkingTag}
          selectedTags={selectedTags}
          onSave={onMenuClose}
          presetColors={presetColors}
        />
      </div>
    );
  }
);

export default TagsSelect;
